import React, { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faYoutube } from "@nnr/fontawesome-classic-brands/icons/classic/brands";
import { faTrash } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Extension } from "@tiptap/core";
import {
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";

import {
  YouTubeVideoNode as CoreYouTubeVideoNode,
  YouTubeVideoAttributes,
} from "@nnr/tiptap-extensions";
import { cn } from "../../../lib/utils";

interface YouTubeVideoProps extends Omit<NodeViewProps, "node"> {
  node: {
    attrs: YouTubeVideoAttributes;
  } & NodeViewProps["node"];
  selected: boolean;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    youtubeVideo: {
      setYouTubeVideo: (options: Partial<YouTubeVideoAttributes>) => ReturnType;
    };
  }
}

const YouTubeVideo: React.FC<YouTubeVideoProps> = ({
  node,
  selected,
  updateAttributes,
  deleteNode,
}) => {
  const [inputValue, setInputValue] = useState<string>(node.attrs.url || "");

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setInputValue(url);
    updateAttributes({ url });
  };

  const isValidYouTubeUrl = (url: string) => {
    const youtubeRegex =
      /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/|youtu\.be\/).+$/;
    return youtubeRegex.test(url);
  };

  const getYouTubeEmbedUrl = (url: string) => {
    if (!url || !isValidYouTubeUrl(url)) return null;

    // Extract video ID from various YouTube URL formats
    const match = url.match(
      /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/,
    );
    if (!match) return null;

    return `https://www.youtube.com/embed/${match[1]}`;
  };

  const embedUrl = getYouTubeEmbedUrl(inputValue);

  return (
    <NodeViewWrapper>
      <div className="group bg-background-2 w-full overflow-hidden rounded">
        {embedUrl && (
          <iframe
            src={embedUrl}
            className="aspect-video"
            width="100%"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
        <div className="youtube-input-container flex items-center gap-2 p-2 text-sm">
          {!embedUrl && <FontAwesomeIcon icon={faYoutube} />}
          <input
            type="url"
            value={inputValue}
            onChange={handleUrlChange}
            placeholder="https://youtube.com/watch?v=..."
            className={cn(
              "shadow-base flex-grow resize-none rounded-[5px] bg-neutral-50 p-2 focus:outline-none",
              {
                selected: selected,
                "outline-1 outline-red-600": !isValidYouTubeUrl(inputValue),
              },
            )}
          />
          <button
            onClick={deleteNode}
            className="hover:bg-background-3 flex h-5 w-5 items-center justify-center rounded-full"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>
    </NodeViewWrapper>
  );
};

export const YouTubeVideoNodeWithView = CoreYouTubeVideoNode.extend<Extension>({
  addNodeView() {
    // @ts-ignore React type compatibility varies between local and CI environments
    return ReactNodeViewRenderer(YouTubeVideo as React.FC<NodeViewProps>);
  },
});
